import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useLocationScope } from "@/context/LocationContext";
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Loader2, History as HistoryIcon, Download, Search } from "lucide-react";
import TermsProofDialog from "@/components/TermsProofDialog";

function fmt(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const STATUSES = ["All", "Captured", "PreAuthorized", "Refunded"];

export default function History() {
  const [rows, setRows] = useState(null);
  const { selectedLot, isAll, currentLot } = useLocationScope();
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("All");
  const [overOnly, setOverOnly] = useState(false);

  useEffect(() => {
    setRows(null);
    const url = isAll ? "/history" : `/history?lotId=${selectedLot}`;
    api.get(url).then(({ data }) => setRows(data));
  }, [selectedLot, isAll]);

  const filtered = useMemo(() => {
    if (!rows) return [];
    const q = search.trim().toLowerCase();
    const fromTs = from ? new Date(from).getTime() : null;
    const toTs = to ? new Date(to).getTime() + 86400000 : null;
    return rows.filter((r) => {
      if (status !== "All" && r.paymentStatus !== status) return false;
      if (overOnly && !r.overstayed) return false;
      if (fromTs && new Date(r.checkOutAt).getTime() < fromTs) return false;
      if (toTs && new Date(r.checkOutAt).getTime() > toTs) return false;
      if (q) {
        const hay = `${r.ticketNumber || ""} ${r.driverName || ""} ${r.companyName || ""} ${r.plate || ""} ${r.mcNumber || ""} ${r.dotNumber || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, search, status, overOnly, from, to]);

  const summary = useMemo(() => {
    const revenue = filtered.reduce((s, r) => s + (r.amountCharged || 0), 0);
    const overstays = filtered.filter((r) => r.overstayed).length;
    const overstayRev = filtered
      .filter((r) => r.overstayed)
      .reduce((s, r) => s + (r.overstayAmount || 0), 0);
    return { count: filtered.length, revenue, overstays, overstayRev };
  }, [filtered]);

  const chartData = useMemo(() => {
    const map = {};
    filtered.forEach((r) => {
      if (!r.checkOutAt) return;
      const d = new Date(r.checkOutAt);
      const key = d.toLocaleDateString([], { month: "short", day: "numeric" });
      const ts = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
      if (!map[key]) map[key] = { day: key, ts, revenue: 0, overstays: 0 };
      map[key].revenue += r.amountCharged || 0;
      if (r.overstayed) map[key].overstays += 1;
    });
    return Object.values(map)
      .sort((a, b) => a.ts - b.ts)
      .map((x) => ({ ...x, revenue: Math.round(x.revenue * 100) / 100 }));
  }, [filtered]);

  const exportCsv = () => {
    const headers = [
      "Ticket #", "Check-in", "Check-out", "Duration (h)", "Driver", "Company", "Truck Type", "Trailer Type",
      "Phone", "Plate", "Spot", "Tenancy", "Amount", "Method", "Status", "Overstay", "MC#", "DOT#", "Lic#",
    ];
    const lines = filtered.map((r) =>
      [
        r.ticketNumber, r.checkInAt, r.checkOutAt, r.durationHours, r.driverName, r.companyName, r.truckType, r.trailerType,
        r.phone, r.plate, r.spotNumber, r.tenancyType, r.amountCharged, r.paymentMethod,
        r.paymentStatus, r.overstayed ? "Yes" : "No", r.mcNumber, r.dotNumber, r.licNumber,
      ]
        .map((v) => `"${v ?? ""}"`)
        .join(",")
    );
    const csv = [headers.join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `lot-history-${Date.now()}.csv`;
    a.click();
  };

  return (
    <div className="px-4 py-6 md:px-8">
      <div className="mb-6 flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-slate-800">
          <HistoryIcon className="h-6 w-6 text-[#A0148C]" />
        </div>
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight text-slate-50">History</h1>
          <p className="text-slate-400">
            {isAll ? "All locations" : currentLot?.name} — completed parking sessions.
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-4 border-slate-800 bg-slate-900 p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div data-testid="history-filter-daterange" className="flex gap-2">
            <div>
              <span className="mb-1 block text-xs text-slate-500">From</span>
              <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-11 border-slate-700 bg-slate-950" />
            </div>
            <div>
              <span className="mb-1 block text-xs text-slate-500">To</span>
              <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-11 border-slate-700 bg-slate-950" />
            </div>
          </div>
          <div className="relative">
            <span className="mb-1 block text-xs text-slate-500">Search</span>
            <Search className="absolute left-3 top-9 h-4 w-4 text-slate-500" />
            <Input
              data-testid="history-filter-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Ticket # / driver / plate / MC# / DOT#"
              className="h-11 w-64 border-slate-700 bg-slate-950 pl-9"
            />
          </div>
          <div>
            <span className="mb-1 block text-xs text-slate-500">Payment status</span>
            <select
              data-testid="history-filter-status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="h-11 rounded-md border border-slate-700 bg-slate-950 px-3 text-slate-100"
            >
              {STATUSES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <button
            data-testid="history-filter-overstay"
            onClick={() => setOverOnly((v) => !v)}
            className={`h-11 rounded-full px-4 text-sm font-medium ${
              overOnly ? "bg-red-600 text-white" : "bg-slate-800 text-slate-300"
            }`}
          >
            Overstays only
          </button>
          <Button
            data-testid="history-export"
            onClick={exportCsv}
            className="ml-auto h-11 gap-2 bg-[#A0148C] hover:bg-[#80106F]"
          >
            <Download className="h-4 w-4" /> Export CSV
          </Button>
        </div>
      </Card>

      {/* Summary */}
      <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4">
        <Sum label="Sessions" value={summary.count} />
        <Sum label="Total Revenue" value={`$${summary.revenue.toFixed(2)}`} cls="text-emerald-400" />
        <Sum label="Overstays" value={summary.overstays} cls={summary.overstays ? "text-red-400" : "text-slate-100"} />
        <Sum label="Overstay Recovered" value={`$${summary.overstayRev.toFixed(2)}`} cls="text-[#A0148C]" />
      </div>

      {/* Insights chart */}
      {chartData.length > 0 && (
        <Card data-testid="history-chart" className="mb-4 border-slate-800 bg-slate-900 p-4">
          <h2 className="mb-3 font-heading text-lg font-bold text-slate-100">Revenue &amp; overstays</h2>
          <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis dataKey="day" stroke="#64748b" fontSize={12} />
                <YAxis yAxisId="left" stroke="#64748b" fontSize={12} />
                <YAxis yAxisId="right" orientation="right" stroke="#64748b" fontSize={12} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 8, color: "#f1f5f9" }} />
                <Legend />
                <Bar yAxisId="left" dataKey="revenue" name="Revenue ($)" fill="#A0148C" radius={[4, 4, 0, 0]} />
                <Line yAxisId="right" type="monotone" dataKey="overstays" name="Overstays" stroke="#ef4444" strokeWidth={2} dot={{ r: 3 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}

      {!rows ? (
        <div className="flex h-[40vh] items-center justify-center">
          <Loader2 className="h-10 w-10 animate-spin text-[#A0148C]" />
        </div>
      ) : (
        <Card className="border-slate-800 bg-slate-900 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-slate-800 hover:bg-transparent">
                {["Ticket #", "Check-in", "Check-out", "Dur", "Driver", "Company", "Truck", "Trailer", "Plate", "Spot", "Tenancy", "Amount", "Method", "Overstay", "MC#", "DOT#", "Lic#"].map((h) => (
                  <TableHead key={h} className="whitespace-nowrap text-slate-400">{h}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((r) => (
                <TableRow key={r.id} data-testid={`history-row-${r.id}`} className="border-slate-800 hover:bg-slate-800/50">
                  <TableCell className="whitespace-nowrap font-data text-xs font-bold text-[#d946ef]">
                    <div className="flex items-center gap-2">{r.ticketNumber || "—"}<TermsProofDialog sessionId={r.id} compact /></div>
                  </TableCell>
                  <TableCell className="whitespace-nowrap font-data text-xs text-slate-300">{fmt(r.checkInAt)}</TableCell>
                  <TableCell className="whitespace-nowrap font-data text-xs text-slate-300">{fmt(r.checkOutAt)}</TableCell>
                  <TableCell className="font-data text-slate-300">{r.durationHours}h</TableCell>
                  <TableCell className="font-semibold text-slate-100">
                    <Link to={`/customers/${r.customerId}`} className="hover:text-[#A0148C] hover:underline">
                      {r.driverName}
                    </Link>
                  </TableCell>
                  <TableCell className="text-slate-300">{r.companyName || "—"}</TableCell>
                  <TableCell className="text-xs text-slate-300">{r.truckType || "—"}</TableCell>
                  <TableCell className="text-xs text-slate-300">{r.trailerType || "—"}</TableCell>
                  <TableCell className="font-data text-slate-300">{r.plate || "—"}</TableCell>
                  <TableCell className="font-data text-slate-300">#{r.spotNumber}</TableCell>
                  <TableCell>
                    <Badge className={r.tenancyType === "Monthly" ? "bg-blue-600" : "bg-slate-700"}>{r.tenancyType}</Badge>
                  </TableCell>
                  <TableCell className="font-data text-emerald-400">${(r.amountCharged || 0).toFixed(2)}</TableCell>
                  <TableCell className="text-slate-300">{r.paymentMethod}</TableCell>
                  <TableCell>
                    {r.overstayed ? <Badge className="bg-red-600">Yes</Badge> : <span className="text-slate-500">No</span>}
                  </TableCell>
                  <TableCell className="font-data text-xs text-slate-300">{r.mcNumber || "—"}</TableCell>
                  <TableCell className="font-data text-xs text-slate-300">{r.dotNumber || "—"}</TableCell>
                  <TableCell className="font-data text-xs text-slate-300">{r.licNumber || "—"}</TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow className="border-slate-800 hover:bg-transparent">
                  <TableCell colSpan={17} className="py-12 text-center text-slate-500">No sessions match your filters.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}

function Sum({ label, value, cls = "text-slate-100" }) {
  return (
    <Card className="border-slate-800 bg-slate-900 p-4">
      <p className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</p>
      <p className={`font-data text-2xl font-bold ${cls}`}>{value}</p>
    </Card>
  );
}
